// src/pages/dashboard/hero-commands/CommandDialog.tsx
import {
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogHeader,
  AlertDialogFooter
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CommandDialogProps } from "@/types/heroCommands";
import {
  useVapiCall,
  usePromptGeneration,
  useUpdateUserHeroCommand,
  useUpdateVapiPrompt
} from "../../../hooks";
import IsLoading from "./IsLoading";
import AdvancedSettings from "./AdvancedSettings";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { useAdvancedSettings, useCommandForm } from "@/hooks/useHeroCommands";

export default function CommandDialog({
  assistantId,
  feature,
  userCommand,
  onOpenChange,
}: CommandDialogProps) {
  const { formData, initialFormData, emptyFields, isLoading, error, inputRefs, handleInputChange, validateForm, isFormValid, resetForm, setError } = useCommandForm({ variables: feature.variables, userCommand });
  const { selectedTool, showAdvanced, customPrompt, setCustomPrompt, toggleAdvanced, handleToolChange, resetSettings } = useAdvancedSettings();
  const { isCalling, isConnecting, error: callError, initiateCall, endCall } = useVapiCall();
  const { generatePrompt, generateCallMetadata } = usePromptGeneration();
  
  const updateUserCommandMutation = useUpdateUserHeroCommand();
  const updateVapiPrompt = useUpdateVapiPrompt();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveCommand = async (variables: Record<string, { value: string }>): Promise<void> => {
    try {
      const currentStatus = userCommand?.is_active ?? false;

      await updateUserCommandMutation.mutateAsync({
        heroCommandId: feature.id,
        variables,
        isActive: currentStatus
      });
      await updateVapiPrompt.mutateAsync({assistantId });  
      toast.success(`Hero Command has been ${userCommand ? 'updated' : 'created'} ${currentStatus && " and activated!"}`);

    } catch (err) {
      console.error('Error updating user command:', err);
      throw err;
    }
  };

  const onSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill in all fields'); // This uses the form's setError
      toast.error('Please fill in all fields before saving.'); // User-facing toast
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const newVariables = Object.entries(formData).reduce((acc, [key, value]) => ({
        ...acc,
        [key]: { value: value.trim() }
      }), {});

      saveCommand(newVariables);
      if (onOpenChange) { 
        onOpenChange(false);
      }
      toast.success('Command saved successfully!');

    } catch (err) {
      console.error('Error saving command:', err);
      const saveErrorMessage = err instanceof Error ? err.message : 'An error occurred while saving.';
      setError(saveErrorMessage); // Set form error
      toast.error(`Save failed: ${saveErrorMessage}`); // User-facing toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestCall = async () => {
    if (!assistantId) return toast.error("Assistant ID is not available. Cannot start test call.");
      
    if (!isFormValid()) { 
      validateForm();
      toast.error("Please fill in all required fields for the hero command.");
      return;
    }
    if (!selectedTool) { 
      toast.error("Please select a test scenario from Advanced Settings to preview the flow.");
      if (!showAdvanced) { 
        toggleAdvanced();
      }
      return;
    }
    if (selectedTool === 'manual_upload' && !customPrompt.trim()) {
      toast.error("Please provide custom instructions for the 'Custom Call' test scenario.");
      return;
    }

    try {
      const { prompt, variables } = generatePrompt(formData, feature, selectedTool, customPrompt);
      const callContextMetadata = generateCallMetadata(feature.id, selectedTool, selectedTool === 'manual_upload' ? customPrompt : undefined);
      await initiateCall(assistantId, prompt, callContextMetadata, variables);
    } catch (err) {
      console.error('Error initiating test call from CommandDialog:', err);
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during test call setup.";
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    resetForm();
    resetSettings();
    if (onOpenChange) { onOpenChange(false); }
  };

  if (isLoading) return <IsLoading />

  const displayError = error || callError;

  const totalFields = Object.keys(feature.variables).length;
  const filledFields = Object.entries(formData).filter(([_, value]) => value && value.trim() !== '').length;
  const modifiedFields = Object.keys(formData).filter(key => formData[key] !== initialFormData[key]).length;
  const errorFields = emptyFields.length;
  const progressPercentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  const isReadyToDeploy = filledFields === totalFields;

  // Determine if the preview button should be disabled
  const isPreviewDisabled = isConnecting || !isFormValid() || !selectedTool;

  return (
    <>
      <AlertDialogContent className="max-w-2xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden">
        <AlertDialogHeader className="flex flex-col shrink-0 p-6 pb-4 border-b border-gray-200">
          <div className="flex flex-row items-start justify-between">
            <div className="flex flex-col gap-1">
              <AlertDialogTitle className="text-lg font-semibold text-gray-900">
                Configure & Deploy
              </AlertDialogTitle>
              <p className="text-sm text-gray-600">
                {feature.title}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span className="text-xs font-medium text-blue-700">{totalFields} Fields</span>
              </div>

              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                <span className="text-xs font-medium text-green-700">{modifiedFields} Modified</span>
              </div>

              {errorFields > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 rounded-full">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  <span className="text-xs font-medium text-red-700">{errorFields} Errors</span>
                </div>
              )}

              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${isReadyToDeploy
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
                }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${isReadyToDeploy ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></div>
                <span className={`text-xs font-medium ${isReadyToDeploy ? 'text-green-700' : 'text-yellow-700'
                  }`}>
                  {isReadyToDeploy ? 'Ready' : `${progressPercentage}%`}
                </span>
              </div>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-1">
          <div className="flex flex-col gap-2">
            {Object.entries(feature.variables).map(([key, variable], index) => {
              const isFilled = formData[key] && formData[key].trim() !== '';
              const isModified = formData[key] !== initialFormData[key];
              const isEmpty = emptyFields.includes(key);

              const isErrorState = isEmpty && !isFilled;
              const isGreen = isFilled && isModified && !isErrorState;
              const isBlue = isFilled && !isModified && !isErrorState;

              return (
                <div
                  key={key}
                  className={`
                    rounded-lg border p-3 transition-all duration-300
                    ${isErrorState
                      ? 'bg-red-50 border-red-200'
                      : isGreen
                        ? 'bg-green-50 border-green-200'
                        : 'border-transparent'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`
                        w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0
                        ${isErrorState
                          ? 'bg-red-500'
                          : isGreen
                            ? 'bg-green-500'
                            : isBlue
                              ? 'bg-blue-600'
                              : 'bg-gray-400'
                        }
                      `}
                    >
                      {isErrorState ? '!' : isGreen ? '✓' : index + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1">
                        <h3
                          className={`
                            text-sm font-semibold capitalize
                            ${isErrorState
                              ? 'text-red-900'
                              : isGreen
                                ? 'text-green-900'
                                : 'text-gray-600'
                            }
                          `}
                        >
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h3>
                        {isGreen && (
                          <span className="text-xs text-green-600 font-medium bg-green-100 px-1.5 py-0.5 rounded-full">
                            Modified
                          </span>
                        )}
                        {isEmpty && (
                          <span className="text-xs text-red-600 font-medium bg-red-100 px-1.5 py-0.5 rounded-full">
                            Required
                          </span>
                        )}
                      </div>

                      <p
                        className={`
                          text-xs mb-2
                          ${isErrorState
                            ? 'text-red-700'
                            : isGreen
                              ? 'text-gray-600'
                              : isBlue
                                ? 'text-blue-700'
                                : 'text-gray-500'
                          }
                        `}
                      >
                        {variable.description}
                      </p>
                      <Textarea
                        ref={el => { if (el) inputRefs.current[key] = el; }}
                        rows={1}
                        placeholder='Type Command'
                        className={`
                          w-full transition-all duration-200 min-h-[38px] h-[38px] scrollbar-none rounded-md text-sm
                          ${isErrorState
                            ? 'border-red-300 bg-white focus:border-red-500 focus:ring-red-500'
                            : isGreen
                              ? 'border-green-300 bg-white focus:border-green-500 focus:ring-green-500'
                              : isBlue
                                ? 'border-blue-300 bg-white focus:border-blue-500 focus:ring-blue-500'
                                : 'border-gray-300 bg-white focus:border-gray-400 focus:ring-gray-400'
                          }
                        `}
                        value={formData[key] || ''}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <AdvancedSettings
          toggleAdvanced={toggleAdvanced}
          showAdvanced={showAdvanced}
          selectedTool={selectedTool}
          handleToolChange={handleToolChange}
          displayError={displayError}
          customPrompt={customPrompt}
          setCustomPrompt={setCustomPrompt}
        />

        <AlertDialogFooter className="gap-2 flex shrink-0 px-8 py-4">

          <AlertDialogCancel onClick={handleCancel}> Cancel </AlertDialogCancel>
          {isCalling ? (
            <Button variant="destructive" className="!ml-auto" onClick={endCall}>
              End Call
            </Button>
          ) : (
            <Button
              className="!ml-auto"
              variant="outline"
              onClick={handleTestCall}
              disabled={isPreviewDisabled} // Use the new disabled condition
              title={isPreviewDisabled && !selectedTool ? "Select a test scenario first" : isPreviewDisabled && !isFormValid() ? "Fill all command fields first" : "Preview this flow"}
            >
              {isConnecting ? <><Phone className="mr-2 h-4 w-4 animate-spin" /> Connecting... </> : <><Phone className="mr-2 h-4 w-4" /> Preview Flow</>}
            </Button>
          )}
          <Button
            onClick={onSubmit}
            disabled={isSubmitting || !isFormValid()}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving... </> : 'Save Changes'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </>
  );
}